import type { AssignmentResponsePublic } from '@repo/types/assignments';
import type { QuizAttemptPublic } from '@repo/types/quiz';

import type { BadgeProps } from '@/components/ui/badge';

/**
 * State of a single assignment attempt as shown in the attempts list.
 *
 * A quiz-backed attempt has two independent things that can be outstanding:
 * the quiz's own scoring (open questions need marking) and the grade on the
 * assignment. Keeping them apart is what makes the row readable.
 *
 * On an auto-graded assignment the grade follows the student's *best* attempt,
 * so a weaker retake stays ungraded for good — 'scored' says the quiz was
 * checked and this attempt simply is not the one carrying the grade.
 */
export type AssignmentAttemptStatus = 'in-progress' | 'awaiting-score' | 'scored' | 'awaiting-grade' | 'graded';

export const ASSIGNMENT_ATTEMPT_BADGE_META: Record<AssignmentAttemptStatus, { label: string; variant: BadgeProps['variant'] }> = {
  'in-progress': { label: 'W trakcie', variant: 'warning' },
  'awaiting-score': { label: 'Oczekuje na sprawdzenie', variant: 'muted' },
  scored: { label: 'Sprawdzone', variant: 'muted' },
  'awaiting-grade': { label: 'Oczekuje na ocenę', variant: 'muted' },
  graded: { label: 'Oceniono', variant: 'brand' },
};

interface AssignmentAttemptStatusOptions {
  /**
   * The quiz attempt behind the submission, when the assignment is quiz-backed
   * and the attempt has been loaded.
   */
  attempt?: QuizAttemptPublic;
  /** Whether the assignment grades itself off the quiz score. */
  autoGrade?: boolean;
}

/**
 * Resolves the status of an assignment attempt.
 *
 * @param submission - The assignment submission row.
 * @param options - The quiz attempt behind it and the assignment's autoGrade flag.
 * @returns The status to badge the row with.
 */
export function getAssignmentAttemptStatus(submission: Pick<AssignmentResponsePublic, 'gradeId'>, options: AssignmentAttemptStatusOptions = {}): AssignmentAttemptStatus {
  const { attempt, autoGrade = false } = options;

  if (attempt) {
    if (!attempt.completed) return 'in-progress';
    if (attempt.score == null) return 'awaiting-score';
  }

  if (submission.gradeId != null) return 'graded';

  // Auto-grading has already run by the time a scored attempt exists, so no
  // grade here means this attempt was not the best one — not that one is due.
  if (autoGrade && attempt?.score != null) return 'scored';

  return 'awaiting-grade';
}

/**
 * Gets the badge metadata for an assignment attempt.
 *
 * @param submission - The assignment submission row.
 * @param options - The quiz attempt behind it and the assignment's autoGrade flag.
 * @returns Object with label and variant for the Badge component.
 */
export function getAssignmentAttemptBadgeMeta(submission: Pick<AssignmentResponsePublic, 'gradeId'>, options: AssignmentAttemptStatusOptions = {}) {
  return ASSIGNMENT_ATTEMPT_BADGE_META[getAssignmentAttemptStatus(submission, options)];
}
